/**
 * Does the server have a newer build than the one this tab is running?
 *
 * Every deploy renames the app's chunks, and an open tab keeps the old
 * names. The tab works until it needs a chunk it has not loaded yet —
 * a lazily loaded modal, a PDF module — and then that file is gone
 * from the server, the import fails, and the page reloads to recover.
 * Whatever was being filled in goes with it. Kyle's tab was open from
 * 4:36 in the morning through four deploys; at 5:55 in the afternoon
 * "Continue to Drafting the Offer" reached for the drafting module,
 * found nothing, and reloaded over a finished offer form.
 *
 * The service worker does not catch this: it checks for a new build on
 * a page load, and a single-page app that is navigated for a whole day
 * never loads a page. So this asks the server directly. index.html
 * names the entry chunk; if the one the server serves differs from the
 * one this tab loaded, a newer build is out, and the shell shows a
 * banner. It never reloads on its own — that is the data loss this
 * exists to prevent — it tells the person, who reloads between tasks.
 */

export const DEFAULT_INTERVAL_MS = 10 * 60 * 1000;
export const MIN_GAP_MS = 90 * 1000;

const ENTRY_PATTERN = /<script[^>]+src="(\/assets\/index-[A-Za-z0-9_-]+\.js)"/;

/** The entry chunk index.html names, or '' when it names none. */
export function entryScriptOf(html) {
  const match = ENTRY_PATTERN.exec(String(html || ''));
  return match ? match[1] : '';
}

/** The entry chunk this document loaded — the same string, read off the page. */
export function currentEntryScript(doc = typeof document !== 'undefined' ? document : null) {
  if (!doc?.scripts) return '';
  for (const script of Array.from(doc.scripts)) {
    const src = script.getAttribute('src') || '';
    if (/^\/assets\/index-[A-Za-z0-9_-]+\.js$/.test(src)) return src;
  }
  return '';
}

/**
 * Decide whether the served page is a newer build than this one. Only
 * a positive answer with both sides known counts: a fetch that fails or
 * a page with no entry (a maintenance page, a proxy error) is not a
 * new build, and must not raise the banner.
 */
export function isNewerBuild(current, served) {
  return Boolean(current) && Boolean(served) && current !== served;
}

/**
 * Start watching. Checks on an interval and whenever the tab comes
 * back into view (a tab left overnight is the case), never more often
 * than MIN_GAP_MS. Calls onUpdate once, with { current, served }, and
 * stops. Returns a stop function.
 */
export function startAppUpdateWatch({
  onUpdate,
  fetchImpl = typeof fetch === 'function' ? fetch : null,
  intervalMs = DEFAULT_INTERVAL_MS,
  minGapMs = MIN_GAP_MS,
  doc = typeof document !== 'undefined' ? document : null,
  now = () => Date.now(),
} = {}) {
  const current = currentEntryScript(doc);
  if (!current || !fetchImpl || typeof onUpdate !== 'function') return () => {};
  let stopped = false;
  let lastCheckAt = 0;
  let timer = null;

  const check = async () => {
    if (stopped) return;
    const at = now();
    if (at - lastCheckAt < minGapMs) return;
    lastCheckAt = at;
    let served = '';
    try {
      // cache: 'no-store' so the answer is the server's, not the service
      // worker's or the browser's copy of the page this tab came from.
      const response = await fetchImpl(`/index.html?update-check=${at}`, { cache: 'no-store', credentials: 'same-origin' });
      if (!response.ok) return;
      served = entryScriptOf(await response.text());
    } catch {
      return; // offline, or a proxy in the way: not a new build
    }
    if (stopped) return;
    if (isNewerBuild(current, served)) {
      stop();
      onUpdate({ current, served });
    }
  };

  const onVisible = () => {
    if (doc?.visibilityState === 'visible') void check();
  };

  function stop() {
    if (stopped) return;
    stopped = true;
    if (timer) clearInterval(timer);
    doc?.removeEventListener?.('visibilitychange', onVisible);
  }

  timer = setInterval(() => { void check(); }, intervalMs);
  doc?.addEventListener?.('visibilitychange', onVisible);
  return stop;
}
